import { useCallback, useEffect, useRef, useState } from 'react'
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded'
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded'
import StopCircleRoundedIcon from '@mui/icons-material/StopCircleRounded'
import StickyNote2RoundedIcon from '@mui/icons-material/StickyNote2Rounded'
import { AppDialog } from '../Common/AppDialog'
import { useClientPreferences } from '../../context/useClientPreferences'
import { useToast } from '../../context/useToast'
import {
  addDebugAttachment,
  addDebugScreenshot,
  appendDebugUserNote,
  getDebugSession,
  removeDebugAttachment,
  removeDebugScreenshot,
  stopDebugSession,
  subscribeDebugSession,
  withDebugRecorderBlackout,
} from '../../utils/debugSessionRecorder'
import { captureDebugScreenshot } from '../../utils/debugScreenshot'
import { useNls } from '../../nls/nlsProvider'
import { isDebugBypassActive, isMockPreviewActive } from '../../debug'

const formatBytes = (bytes: number) => {
  if (bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  const idx = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)))
  const value = bytes / 1024 ** idx
  return `${value.toFixed(value >= 10 || idx === 0 ? 0 : 1)} ${units[idx]}`
}

const formatElapsed = (ms: number) => {
  const total = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  if (m <= 0) return `${s}s`
  return `${m}m ${s.toString().padStart(2, '0')}s`
}

export const DebugModeIndicator = () => {
  const { t } = useNls()
  const { preferences, updatePreferences } = useClientPreferences()
  const { showToast } = useToast()
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [endDialogOpen, setEndDialogOpen] = useState(false)
  const [noteDrawerOpen, setNoteDrawerOpen] = useState(false)
  const [note, setNote] = useState('')
  const [isCapturing, setIsCapturing] = useState(false)
  const [isEnding, setIsEnding] = useState(false)
  const [, forceRender] = useState(0)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => subscribeDebugSession(() => forceRender((n) => n + 1)), [])

  // Tick every second so the elapsed counter on the pill updates in place.
  useEffect(() => {
    if (!preferences.enableDebugMode) return
    const timer = window.setInterval(() => forceRender((n) => n + 1), 1000)
    return () => window.clearInterval(timer)
  }, [preferences.enableDebugMode])

  useEffect(() => {
    if (!popoverOpen) return
    const handleClick = (event: MouseEvent) => {
      if (!(event.target instanceof Node)) return
      if (popoverRef.current?.contains(event.target)) return
      if (buttonRef.current?.contains(event.target)) return
      setPopoverOpen(false)
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPopoverOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [popoverOpen])

  const session = getDebugSession()

  const doCaptureScreenshot = useCallback(async () => {
    if (isCapturing) return
    setIsCapturing(true)
    try {
      const capture = await captureDebugScreenshot()
      if (!capture) {
        showToast(t('NLSExperienceDebugCaptureFailed'), 'warning')
        return
      }
      const result = await addDebugScreenshot({
        blob: capture.blob,
        width: capture.width,
        height: capture.height,
        capturedAt: capture.capturedAt,
        source: 'manual',
      })
      if (!result.ok) {
        showToast(result.reason === 'quota' ? t('NLSExperienceDebugQuotaReached') : t('NLSExperienceDebugScreenshotSaveFailed'), 'warning')
        return
      }
      showToast(t('NLSExperienceDebugScreenshotAdded'), 'success')
    } finally {
      setIsCapturing(false)
    }
  }, [isCapturing, showToast, t])

  const doAttachFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return
    let added = 0
    for (const file of Array.from(files)) {
      const result = await addDebugAttachment({ file })
      if (result.ok) added += 1
      else if (result.reason === 'quota') {
        showToast(t('NLSExperienceDebugQuotaReached'), 'warning')
        break
      }
    }
    if (added > 0) {
      showToast(t('NLSExperienceDebugAttachedFilesToast', { count: added, plural: added === 1 ? '' : 's' }), 'success')
    }
  }, [showToast, t])

  const doAppendNote = useCallback(() => {
    const trimmed = note.trim()
    if (!trimmed) return
    appendDebugUserNote(trimmed)
    setNote('')
    setNoteDrawerOpen(false)
    showToast(t('NLSExperienceDebugNoteAdded'), 'success')
  }, [note, showToast, t])

  const openEndConfirmation = () => {
    setPopoverOpen(false)
    setEndDialogOpen(true)
  }

  const cancelEnd = () => {
    if (isEnding) return
    setEndDialogOpen(false)
  }

  const finishAndReturnToSupport = () => {
    if (isEnding) return
    setIsEnding(true)
    try {
      // Stop the session so no further clicks (including the navigation
      // dispatched below) get recorded, then hand routing off to App.tsx.
      // The session data stays in local storage so SupportPanel can attach
      // it to the user's new ticket draft on the next screen.
      withDebugRecorderBlackout(() => {
        stopDebugSession()
        updatePreferences({ enableDebugMode: false })
        window.dispatchEvent(new CustomEvent('timelining:openUserSettingsSupport'))
      })
      setEndDialogOpen(false)
      setPopoverOpen(false)
      showToast('Capture ended. Review your draft in Support and submit when ready.', 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not end the capture.', 'error')
    } finally {
      setIsEnding(false)
    }
  }

  const isMock = isMockPreviewActive()
  const isDebug = isDebugBypassActive()
  const hasPreviewPill = isMock || isDebug
  const hasDiagnosticCapture = preferences.enableDebugMode

  if (!hasPreviewPill && !hasDiagnosticCapture) return null

  const previewPill = hasPreviewPill ? (
    <span
      className={`previewModePill ${isMock ? 'previewModePill--mock' : 'previewModePill--debug'}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 28,
        padding: '0 10px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: 0.5,
        border: `1px solid ${isMock ? 'var(--warning-soft)' : 'var(--info-soft)'}`,
        background: isMock ? 'var(--warning-soft)' : 'var(--info-soft)',
        color: isMock ? 'color-mix(in srgb, var(--warning) 60%, var(--text-strong))' : 'color-mix(in srgb, var(--info) 60%, var(--text-strong))',
        userSelect: 'none',
      }}
      title={isMock ? 'Mock mode — no writes persist' : 'Emulator-backed — writes persist in emulator session'}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: isMock ? 'var(--warning)' : 'var(--info)',
        }}
        aria-hidden="true"
      />
      {isMock ? 'MOCK' : 'DEBUG'}
    </span>
  ) : null

  if (hasPreviewPill && !hasDiagnosticCapture) {
    return (
      <div className="debugModeHost" data-debug-ignore="true">
        {previewPill}
      </div>
    )
  }

  const totals = {
    events: session?.events.length ?? 0,
    screenshots: session?.screenshots.length ?? 0,
    attachments: session?.attachments.length ?? 0,
    network: session?.networkCount ?? 0,
    bytes: (session?.screenshots.reduce((s, x) => s + x.bytes, 0) ?? 0)
      + (session?.attachments.reduce((s, x) => s + x.bytes, 0) ?? 0),
  }
  const elapsedMs = session?.startedAt ? Date.now() - new Date(session.startedAt).getTime() : 0
  const elapsedLabel = formatElapsed(elapsedMs)

  return (
    <>
      <div className="debugModeHost" data-debug-ignore="true" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {previewPill}
        <button
          ref={buttonRef}
          type="button"
          className="debugModeIndicator"
          onClick={() => setPopoverOpen((prev) => !prev)}
          aria-label={t('NLSExperienceDebugModeActiveAria')}
          aria-expanded={popoverOpen}
          title={t('NLSExperienceDebugModeActiveTitle')}
        >
          <span className="debugModeIndicator__pulse" aria-hidden="true" />
          <span className="debugModeIndicator__label">{t('NLSExperienceDebug')}</span>
          <span className="debugModeIndicator__sep" aria-hidden="true" />
          <span className="debugModeIndicator__stat">
            <b>{totals.events}</b>
            <small>events</small>
          </span>
          <span className="debugModeIndicator__sep" aria-hidden="true" />
          <span className="debugModeIndicator__stat">
            <b>{elapsedLabel}</b>
            <small>elapsed</small>
          </span>
        </button>
        {popoverOpen && (
          <div ref={popoverRef} className="debugPopover" role="dialog" aria-label={t('NLSExperienceDebugCapturePanelAria')}>
            <header className="debugPopover__header">
              <div>
                <strong>{t('NLSExperienceDebugCapture')}</strong>
                <small>{t('NLSExperienceDebugSession', { id: session?.sessionId.slice(-8) ?? '-' })}</small>
              </div>
              <button
                type="button"
                className="debugPopover__close"
                onClick={() => setPopoverOpen(false)}
                aria-label={t('NLSExperienceDebugClosePanelAria')}
              ><CloseRoundedIcon fontSize="inherit" /></button>
            </header>

            <ul className="debugPopover__counters">
              <li><span>{totals.events}</span><small>{t('NLSExperienceDebugCounterEvents')}</small></li>
              <li><span>{totals.screenshots}</span><small>{t('NLSExperienceDebugCounterShots')}</small></li>
              <li><span>{totals.attachments}</span><small>{t('NLSExperienceDebugCounterFiles')}</small></li>
              <li><span>{totals.network}</span><small>{t('NLSExperienceDebugCounterNet')}</small></li>
              <li><span>{formatBytes(totals.bytes)}</span><small>{t('NLSExperienceDebugCounterBundle')}</small></li>
              <li><span>{elapsedLabel}</span><small>{t('NLSExperienceDebugCounterElapsed')}</small></li>
            </ul>

            <div className="debugPopover__quickRow" role="group" aria-label="Quick capture actions">
              <button
                type="button"
                className="debugPopover__quickBtn"
                onClick={() => void doCaptureScreenshot()}
                disabled={isCapturing}
              >
                <PhotoCameraRoundedIcon fontSize="small" />
                <span>{isCapturing ? t('NLSExperienceDebugCapturing') : t('NLSExperienceDebugCaptureAction')}</span>
              </button>
              <button
                type="button"
                className="debugPopover__quickBtn"
                onClick={() => fileInputRef.current?.click()}
              >
                <AttachFileRoundedIcon fontSize="small" />
                <span>{t('NLSExperienceDebugAddFiles')}</span>
              </button>
              <button
                type="button"
                className={`debugPopover__quickBtn${noteDrawerOpen ? ' isActive' : ''}`}
                onClick={() => setNoteDrawerOpen((next) => !next)}
                aria-expanded={noteDrawerOpen}
              >
                <StickyNote2RoundedIcon fontSize="small" />
                <span>{t('NLSExperienceDebugAppendNote')}</span>
              </button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                void doAttachFiles(e.target.files)
                if (fileInputRef.current) fileInputRef.current.value = ''
              }}
            />

            {noteDrawerOpen && (
              <div className="debugPopover__noteDrawer">
                <textarea
                  rows={3}
                  autoFocus
                  placeholder={t('NLSExperienceDebugNotePlaceholder')}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <div className="debugPopover__noteActions">
                  <button
                    type="button"
                    className="debugPopover__ghostBtn"
                    onClick={() => { setNote(''); setNoteDrawerOpen(false) }}
                  >Cancel</button>
                  <button
                    type="button"
                    className="debugPopover__primaryBtn debugPopover__primaryBtn--sm"
                    onClick={doAppendNote}
                    disabled={!note.trim()}
                  >
                    <AddCircleOutlineRoundedIcon fontSize="inherit" />
                    {t('NLSExperienceDebugAppendNote')}
                  </button>
                </div>
              </div>
            )}

            {(totals.screenshots > 0 || totals.attachments > 0) && (
              <section className="debugPopover__section">
                <label className="debugPopover__label">{t('NLSExperienceDebugInBundle')}</label>
                <ul className="debugPopover__manifest">
                  {session?.screenshots.map((s) => (
                    <li key={s.id}>
                      <span><PhotoCameraRoundedIcon fontSize="inherit" /> {s.label || `${s.source} shot`} · {formatBytes(s.bytes)}</span>
                      <button type="button" onClick={() => void removeDebugScreenshot(s.id)} aria-label={t('NLSExperienceDebugRemoveScreenshotAria')}><CloseRoundedIcon fontSize="inherit" /></button>
                    </li>
                  ))}
                  {session?.attachments.map((a) => (
                    <li key={a.id}>
                      <span><AttachFileRoundedIcon fontSize="inherit" /> {a.filename} · {formatBytes(a.bytes)}</span>
                      <button type="button" onClick={() => void removeDebugAttachment(a.id)} aria-label={t('NLSExperienceDebugRemoveAttachmentAria')}><CloseRoundedIcon fontSize="inherit" /></button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <button
              type="button"
              className="debugPopover__primaryBtn debugPopover__endBtn"
              onClick={openEndConfirmation}
            >
              <StopCircleRoundedIcon fontSize="small" />
              End capture &amp; return to Support
            </button>
          </div>
        )}
      </div>

      <AppDialog
        open={endDialogOpen}
        title="End capture and return to Support?"
        message={(
          <div className="debugModeEndDialog" data-debug-ignore="true">
            <p>
              We&apos;ll stop recording and take you back to Support. Your session with
              <strong> {totals.events} events</strong> and
              <strong> {totals.screenshots} screenshot{totals.screenshots === 1 ? '' : 's'}</strong>
              {' '}stays attached — write a title, description, and severity, then submit
              to the owner when you&apos;re ready.
            </p>
            <dl className="debugModeEndDialog__grid">
              <div><dt>Events</dt><dd>{totals.events}</dd></div>
              <div><dt>Screens</dt><dd>{totals.screenshots}</dd></div>
              <div><dt>Files</dt><dd>{totals.attachments}</dd></div>
              <div><dt>Network</dt><dd>{totals.network}</dd></div>
              <div><dt>Bundle</dt><dd>{formatBytes(totals.bytes)}</dd></div>
              <div><dt>Elapsed</dt><dd>{elapsedLabel}</dd></div>
            </dl>
          </div>
        )}
        actions={[
          { label: 'Keep capturing', variant: 'ghost', onClick: cancelEnd, disabled: isEnding },
          { label: isEnding ? 'Ending…' : 'End & return', variant: 'primary', onClick: finishAndReturnToSupport, disabled: isEnding, loading: isEnding },
        ]}
        onDismiss={cancelEnd}
      />
    </>
  )
}
